var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { SignalWatcher } from "@lit-labs/preact-signals";
import "../components/code-input";
import { draft } from "../state";
let ModeAudit = class ModeAudit extends SignalWatcher(LitElement) {
    constructor() {
        super(...arguments);
        this.presented = [];
        this.edition = "13e";
    }
    static { this.styles = css `
    :host, *, *::before, *::after { box-sizing: border-box; }
    :host { display: block; }
    .intro h2 { font-family: var(--font-display); font-size: 13px; font-weight: 700; margin: 0 0 3px;
                text-transform: uppercase; letter-spacing: .05em; color: var(--color-slate-700); }
    .intro .sub { font-size: 12px; color: var(--color-slate-500); margin-bottom: 14px; line-height: 1.45; }
    .row { padding: 10px 12px; border: 1px solid var(--color-slate-200); border-radius: var(--radius-md);
           background: var(--color-white); margin-bottom: 8px; }
    .top { display: flex; align-items: center; gap: 10px; }
    .code { font-family: var(--font-mono); font-weight: 700; font-size: 14px; color: var(--color-slate-900); }
    .desc { font-size: 13px; color: var(--color-slate-700); flex: 1; }
    .verdict { display: flex; gap: 6px; margin-left: auto; }
    .vbtn { padding: 5px 12px; border-radius: var(--radius-sm); border: 1px solid var(--color-slate-300);
            background: var(--color-white); cursor: pointer; font: inherit; font-size: 12px; font-weight: 600;
            color: var(--color-slate-600); white-space: nowrap; }
    .vbtn.keep.active { background: var(--color-green-500, #16A34A); color: white; border-color: var(--color-green-500, #16A34A); }
    .vbtn.wrong.active { background: #DC2626; color: white; border-color: #DC2626; }
    .replace { margin-top: 10px; }
    .replace label { font-size: 11px; color: var(--color-slate-500); text-transform: uppercase; letter-spacing: .04em;
                     font-weight: 600; display: block; margin-bottom: 4px; }
    .replace .chosen { font-family: var(--font-mono); font-weight: 700; color: var(--color-blue-600); font-size: 13px; }
    .add { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--color-slate-200); }
    .add h3 { font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: var(--color-slate-700);
              margin: 0 0 4px; }
    .add .sub { font-size: 12px; color: var(--color-slate-500); margin-bottom: 8px; }
    .added { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
    .added .pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 8px; border-radius: 12px;
                   background: var(--color-blue-100); color: var(--color-blue-600); font-family: var(--font-mono);
                   font-size: 12px; font-weight: 600; }
    .added .pill button { background: none; border: 0; color: var(--color-blue-600); cursor: pointer; font-size: 14px;
                          line-height: 1; padding: 0; }
  `; }
    actions() { return (draft.value?.audit_actions) ?? {}; }
    setAction(code, action) {
        const d = draft.value;
        const prev = this.actions()[code] ?? { action, replacement_code: null };
        const audit_actions = { ...this.actions(), [code]: { ...prev, action, replacement_code: action === "keep" ? null : prev.replacement_code } };
        draft.value = { ...d, audit_actions };
    }
    setReplacement(code, replacement) {
        const d = draft.value;
        const prev = this.actions()[code] ?? { action: "wrong", replacement_code: null };
        const audit_actions = { ...this.actions(), [code]: { ...prev, action: "wrong", replacement_code: replacement || null } };
        draft.value = { ...d, audit_actions };
    }
    addedCodes() { return (draft.value?.added_codes) ?? []; }
    addMissing(code) {
        if (this.addedCodes().some((c) => c.code === code))
            return;
        const d = draft.value;
        draft.value = { ...d, added_codes: [...this.addedCodes(), { code }] };
    }
    removeAdded(code) {
        const d = draft.value;
        draft.value = { ...d, added_codes: this.addedCodes().filter((c) => c.code !== code) };
    }
    render() {
        const aa = this.actions();
        return html `
      <div class="intro">
        <h2>Audit the coded episode</h2>
        <div class="sub">For each presented code, keep it or mark it wrong. If wrong, supply the correct replacement. Add any required codes that are missing.</div>
      </div>
      ${this.presented.map((it) => {
            const cur = aa[it.code] ?? {};
            const title = it.title ?? it.description;
            return html `
          <div class="row">
            <div class="top">
              <span class="code">${it.code}</span>
              ${title ? html `<span class="desc">${title}</span>` : html `<span class="desc"></span>`}
              <div class="verdict">
                <button class="vbtn keep ${cur.action === "keep" ? "active" : ""}"
                        data-action="keep" data-code=${it.code}
                        @click=${() => this.setAction(it.code, "keep")}>✓ Keep</button>
                <button class="vbtn wrong ${cur.action === "wrong" ? "active" : ""}"
                        data-action="wrong" data-code=${it.code}
                        @click=${() => this.setAction(it.code, "wrong")}>✗ Wrong</button>
              </div>
            </div>
            ${cur.action === "wrong" ? html `
              <div class="replace">
                <label>Replacement code${cur.replacement_code ? html `: <span class="chosen">${cur.replacement_code}</span>` : ""}</label>
                <code-input .edition=${this.edition} placeholder="Type the correct code (e.g. E11.71 or 30473-00)"
                            @code-pick=${(e) => this.setReplacement(it.code, e.detail.code)}></code-input>
              </div>` : ""}
          </div>`;
        })}
      <div class="add">
        <h3>Add missing codes</h3>
        <div class="sub">If a required code is absent from the set above, add it here.</div>
        <code-input .edition=${this.edition} placeholder="Type a code to add (e.g. E11.71 or 30473-00)"
                    @code-pick=${(e) => this.addMissing(e.detail.code)}></code-input>
        <div class="added">
          ${this.addedCodes().map((c) => html `
            <span class="pill">${c.code}
              <button aria-label="Remove ${c.code}" data-rm-add=${c.code} @click=${() => this.removeAdded(c.code)}>×</button>
            </span>`)}
        </div>
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], ModeAudit.prototype, "presented", void 0);
__decorate([
    property({ type: String })
], ModeAudit.prototype, "edition", void 0);
ModeAudit = __decorate([
    customElement("mode-audit")
], ModeAudit);
export { ModeAudit };
